<!-- 推送测试页面 -->
<template>
  <div class="step-page push-test">
    <div class="step-container">
      <!-- 步骤进度条 -->
      <module-container title="制作步骤">
        <step-progress
          :current-step="$route.query.type == 3 ? 3 : 4"
          :steps="progressPages"
        />
      </module-container>
      <!-- 主要内容区域 -->
      <div class="step-content">
        <module-container title="推送测试" :loading="initLoading">
          <div class="push-test-form">
            <el-form
              ref="testForm"
              :model="testConfig"
              :rules="rules"
              label-width="120px"
            >
              <!-- 邮件标题 -->
              <el-form-item label="邮件标题">
                <el-input
                  v-model="emailTitle"
                  disabled
                  placeholder="邮件标题"
                />
              </el-form-item>
              <!-- 测试邮箱 -->
              <el-form-item label="推送测试邮箱" prop="testEmail">
                <el-input
                  v-model="testConfig.testEmail"
                  placeholder="请填写推送测试邮箱地址"
                  clearable
                  :disabled="saveLoading"
                />
              </el-form-item>
            </el-form>
            <!-- 操作按钮 -->
            <div class="action-buttons">
              <el-button
                type="primary"
                :loading="saveLoading"
                @click="handleSave"
              >
                推送测试
              </el-button>
              <el-button :disabled="saveLoading" @click="handlePrev">
                上一步
              </el-button>
              <el-button :disabled="saveLoading" @click="handleNext">
                下一步
              </el-button>
            </div>
          </div>
        </module-container>
      </div>
    </div>
  </div>
</template>
<script>
import ModuleContainer from '@/components/moduleContainer'
import StepProgress from '@/components/stepProgress'
import { Get, ProjectConfig } from '@/api'
import { mapState } from 'vuex'

// 邮箱验证正则
const EMAIL_REG = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * 推送测试页面组件
 * 功能：第五步 - 发送测试邮件，确认推送效果
 * - 自动获取推送模板ID
 * - 调用试推送接口
 */
export default {
  name: 'PushTest',
  components: {
    ModuleContainer,
    StepProgress
  },
  data() {
    // 邮箱验证规则
    const validateEmail = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入测试邮箱'))
      } else if (!EMAIL_REG.test(value)) {
        callback(new Error('请输入有效的邮箱地址'))
      } else {
        callback()
      }
    }
    return {
      loading: false, // 初始化loading（获取推送模板时使用）
      initLoading: false, // 组件整体loading
      saveLoading: false, // 按钮loading（推送测试时使用）
      pushHistoryTemplateId: null, // 推送模板ID
      testConfig: {
        testEmail: ''
      },
      rules: {
        testEmail: [
          { required: true, validator: validateEmail, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapState('incrementService', ['progressPages']),
    // 从 store 获取当前任务信息
    taskInfo() {
      return this.$store.getters['incrementService/getCurrentTaskInfo']
    },
    // 邮件标题
    emailTitle() {
      return this.taskInfo?.topic || ''
    },
    // 任务ID
    taskId() {
      return this.$route.query.taskId || this.taskInfo?.id || null
    }
  },
  mounted() {
    this.initData()
  },
  methods: {
    /**
     * 初始化数据：获取推送模板ID
     */
    async initData() {
      if (!this.taskId) {
        this.$message.warning('缺少任务ID参数')
        return
      }
      await this.fetchPushTemplate()
    },

    /**
     * 获取推送模板
     * API: GET /api/task/template/getList?historyId={taskId}
     */
    async fetchPushTemplate() {
      this.initLoading = true
      try {
        const res = await Get(ProjectConfig.increment.GetTaskPushTemplate.url, {
          params: { historyId: this.taskId }
        })

        if (res?.data?.status === 0) {
          const templateData = res.data.data
          if (Array.isArray(templateData) && templateData.length > 0) {
            this.pushHistoryTemplateId = templateData[0].id
          }

          if (!this.pushHistoryTemplateId) {
            console.warn('未获取到推送模板ID，返回数据：', templateData)
          }
        } else {
          this.$message.error(res?.data?.message || '获取推送模板失败')
        }
      } catch (error) {
        console.error('获取推送模板失败：', error)
        this.$message.error('获取推送模板失败，请稍后重试')
      } finally {
        this.initLoading = false
      }
    },

    /**
     * 发送测试推送
     * API: GET /api/task/push/trySend
     */
    async handleSave() {
      // 表单验证
      try {
        await this.$refs.testForm.validate()
      } catch (error) {
        return
      }
      if (!this.taskId) {
        this.$message.error('缺少任务ID')
        return
      }
      if (!this.pushHistoryTemplateId) {
        this.$message.error('推送模板ID未获取，请刷新页面重试')
        return
      }

      this.saveLoading = true
      const namespace = window.location.origin
      try {
        const res = await Get(ProjectConfig.increment.TryPush.url, {
          params: {
            taskId: this.taskId,
            email: this.testConfig.testEmail,
            pushHistoryTemplateId: this.pushHistoryTemplateId,
            namespace: namespace + '/das-manager'
          }
        })

        if (res?.data?.status === 0) {
          this.$message.success('推送成功')
        } else {
          this.$alert(res?.data?.message || '推送失败', '提示', {
            type: 'error',
            confirmButtonText: '确定'
          })
        }
      } catch (error) {
        this.$alert('推送失败，请稍后重试', '提示', {
          type: 'error',
          confirmButtonText: '确定'
        })
      } finally {
        this.saveLoading = false
      }
    },

    /**
     * 页面导航：构建带 taskId 的路由
     */
    navigateTo(path) {
      this.$router.push({
        path,
        query: {
          ...this.$route.query
        }
      })
    },

    /**
     * 下一步：跳转到正式推送页面
     */
    handleNext() {
      this.navigateTo('/steps/officialPush')
    },

    /**
     * 上一步：跳转到查看学者页面
     */
    handlePrev() {
      this.navigateTo('/steps/viewScholars')
    }
  }
}
</script>
<style lang="scss" scoped>
.step-page {
  height: 100%;
  overflow: hidden;
  background: #f5f7fd;

  .step-container {
    height: 100%;
    overflow-y: auto;

    .step-content {
      ::v-deep(.module-body) {
        min-height: 300px !important;
      }

      .push-test-form {
        display: flex;
        flex-direction: column;
        align-items: center;

        .el-form {
          width: 100%;
          max-width: 500px;
        }

        ::v-deep .el-form-item {
          margin-bottom: 22px;
        }

        ::v-deep .el-form-item__label {
          text-align: right;
          padding-right: 12px;
          font-size: 14px;
        }

        .el-input {
          width: 400px;
          font-size: 14px;
        }

        .action-buttons {
          margin-top: 30px;
          text-align: center;

          .el-button {
            margin: 0 10px;
            min-width: 100px;
          }
        }
      }
    }
  }
}
</style>
